import React, { useState } from 'react';
import ProductionComponentForm from './ProductionComponentForm';

const ProductionComponentsManager = ({ categoryId, categoryName, initialComponents, onSave, saving }) => {
    const [components, setComponents] = useState(initialComponents || []);
    const [showForm, setShowForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    const addComponent = (component) => {
        const newComponents = [...components, { ...component, sortOrder: components.length }];
        setComponents(newComponents);
        setShowForm(false);
    };

    const updateComponent = (index, component) => {
        const newComponents = [...components];
        newComponents[index] = { ...newComponents[index], ...component };
        setComponents(newComponents);
        setEditingIndex(null);
        setShowForm(false);
    };

    const deleteComponent = (index) => {
        if (window.confirm('Are you sure you want to delete this component?')) {
            const newComponents = components.filter((_, i) => i !== index);
            setComponents(newComponents);
        }
    };

    const moveComponent = (index, direction) => {
        const newComponents = [...components];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < components.length) {
            [newComponents[index], newComponents[newIndex]] = [newComponents[newIndex], newComponents[index]];
            // Update sort orders
            newComponents.forEach((comp, idx) => { comp.sortOrder = idx; });
            setComponents(newComponents);
        }
    };

    const handleSave = () => {
        onSave(components);
    };

    return (
        <div className="production-components-manager">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Production Components for {categoryName}</h6>
                <div>
                    <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => {
                            setEditingIndex(null);
                            setShowForm(true);
                        }}
                    >
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Component
                    </button>
                    <button
                        className="btn btn-sm btn-success"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save All Components'}
                    </button>
                </div>
            </div>

            <p className="text-muted small mb-3">
                Define what components (fabrics, threads, zari, etc.) products in this category can have.
                These will appear in the product specifications tab when creating/editing products.
            </p>

            {/* Component Form Modal */}
            {showForm && (
                <ProductionComponentForm
                    initialData={editingIndex !== null ? components[editingIndex] : null}
                    onSave={(data) => {
                        if (editingIndex !== null) {
                            updateComponent(editingIndex, data);
                        } else {
                            addComponent(data);
                        }
                    }}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingIndex(null);
                    }}
                />
            )}

            {/* Components List */}
            {components.length === 0 ? (
                <div className="text-center text-muted py-4 bg-light rounded">
                    <i className="bi bi-box fs-1"></i>
                    <p className="mt-2 mb-0">No production components defined yet.</p>
                    <small>Click "Add Component" to define what goes into products of this category.</small>
                </div>
            ) : (
                <div className="row">
                    {components.map((component, idx) => (
                        <div key={idx} className="col-12 mb-3">
                            <div className="card border">
                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>{component.label}</strong>
                                        <span className="badge bg-secondary ms-2">{component.name}</span>
                                        {component.allowsMultiple && (
                                            <span className="badge bg-info ms-2">Multiple Allowed</span>
                                        )}
                                    </div>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-1"
                                            onClick={() => moveComponent(idx, 'up')}
                                            disabled={idx === 0}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-1"
                                            onClick={() => moveComponent(idx, 'down')}
                                            disabled={idx === components.length - 1}
                                        >
                                            ↓
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary me-1"
                                            onClick={() => {
                                                setEditingIndex(idx);
                                                setShowForm(true);
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => deleteComponent(idx)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {component.description && (
                                        <p className="text-muted small mb-2">{component.description}</p>
                                    )}
                                    <div className="row">
                                        <div className="col-md-6">
                                            <small className="text-muted">Fields:</small>
                                            <ul className="small mb-0">
                                                {component.fields.map((field, fIdx) => (
                                                    <li key={fIdx}>
                                                        {field.label} ({field.type})
                                                        {field.required && <span className="text-danger ms-1">*</span>}
                                                        {field.unit && <span className="text-muted ms-1">[{field.unit}]</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        {component.fields.some(f => f.type === 'MATERIAL_SELECT') && (
                                            <div className="col-md-6">
                                                <small className="text-muted">Material Sources:</small>
                                                <ul className="small mb-0">
                                                    {component.fields
                                                        .filter(f => f.type === 'MATERIAL_SELECT')
                                                        .map((field, fIdx) => (
                                                            <li key={fIdx}>
                                                                {field.label}: {field.rawMaterialCategorySlug || 'Any category'}
                                                            </li>
                                                        ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductionComponentsManager;